import { clientEnv } from 'config/env';
import Header from 'app/components/partials/header.container';
import Helmet from 'react-helmet';
import Loader from '../partials/loader';
import NotFound from './error/not-found-page';
import { useParams } from 'react-router-dom';
import React, { useEffect, useRef } from 'react';

// Hardcode supported venues. Credentials are per venue: Western Springs uses its
// own MappedIn key/secret, Go Media Stadium keeps the shared pair.
const venueMapping = {
    'go-media-stadium': {
        name: 'Go Media Stadium',
        mappedinVenue: 'auckland-go-media-stadium', // provided by mappedin
        mappedinId: clientEnv.MAPPEDIN_ID,
        mappedinKey: clientEnv.MAPPEDIN_KEY,
    },
    'western-springs-bowl': {
        name: 'Western Springs Bowl',
        mappedinVenue: 'auckland-western-springs-bowl-concert', // provided by mappedin
        mappedinId: clientEnv.MAPPEDIN_WESTERN_SPRINGS_ID,
        mappedinKey: clientEnv.MAPPEDIN_WESTERN_SPRINGS_KEY,
    }
};

const MappedinPage = () => {
    const { venue: venueSlug } = useParams();
    const pageRef = useRef(null);

    // Return 404 for unsupported venues
    const venue = venueMapping[venueSlug];

    // Ensure we load the mappedin script once DOM is ready
    useEffect(() => {
        if (!venue) return undefined;

        const syncMapLayout = () => {
            const pageEl = pageRef.current;

            if (!pageEl) return;

            // The headroom wrapper already reserves the header's height in normal
            // flow. Only shrink the map by that amount — an extra margin leaves a
            // blank band under the header. visualViewport excludes the mobile URL bar.
            const headerSpace = document.querySelector('.headroom-wrapper');
            const offset = headerSpace ? headerSpace.offsetHeight : 0;
            const visibleHeight = Math.round(window.visualViewport?.height || window.innerHeight);

            pageEl.style.height = `${Math.max(visibleHeight - offset, 0)}px`;
        };

        syncMapLayout();

        // Script to configure mappedin
        const initScript = document.createElement('script');
        initScript.innerHTML = `window.mappedin = {
            miKey: {
                id: '${venue.mappedinId}',
                key: '${venue.mappedinKey}',
            },
            featureFlags: {
                multiLayoutEnabled: true,
            },
            venue: '${venue.mappedinVenue}',
        };`; // Inline script content

        // External Mappedin vendor script
        const externalScript = document.createElement('script');
        externalScript.src = `https://cdn.mappedin.com/web2/release/mappedin-web.js?venue=${venue.mappedinVenue}`;
        externalScript.id = 'mappedin-web';
        externalScript.type = 'module';
        document.body.appendChild(initScript);
        document.body.appendChild(externalScript);

        // Init if navigated to this page via Router
        if (window.mappedinManager && window.mappedinManager.init) {
            window.mappedinManager.init();
        }

        const headroom = document.querySelector('.headroom-wrapper');
        const resizeObserver = new ResizeObserver(syncMapLayout);

        if (headroom) resizeObserver.observe(headroom);

        window.addEventListener('resize', syncMapLayout);
        window.visualViewport?.addEventListener('resize', syncMapLayout);

        return () => {
            // Need to teardown mappedin if navigating away via Router
            if (window.mappedinManager) {
                window.mappedinManager.teardown();
            }
            document.body.removeChild(externalScript);
            document.body.removeChild(initScript);
            resizeObserver.disconnect();
            window.removeEventListener('resize', syncMapLayout);
            window.visualViewport?.removeEventListener('resize', syncMapLayout);
        };
    }, [venueSlug]);

    if (!venue) {
        return <NotFound />;
    }

    const venueName = venue.name;

    return (
        <>
            <Header />
            <main className="mappedin-page" role="main" ref={pageRef}>
                <Helmet>
                    <title>{venueName} | Virtual Tour</title>
                    <meta name="description" content={`Take a virtual tour of ${venueName}`} />
                    <meta name="keywords" content={`${venueName}, virtual tour, venue map`} />
                    <link href={`https://cdn.mappedin.com/web2/release/mappedin-web.css?venue=${venue.mappedinVenue}`} rel="stylesheet" />
                </Helmet>
                <div data-key="externalId" id="mappedin-map">
                    {/* display loader while mappedin external script is loading */}
                    <Loader type="center-loader"/>
                </div>
            </main>
        </>
    );
};

export default MappedinPage;
